/**
 * BirthdayPopup — celebratory popup shown when the user opens the app after
 * clicking the birthday push notification (deep-link `?birthday=1`).
 *
 * Style aligned with LatestVictoryPopup (confetti bursts + gold gradient).
 * Message-only surprise: no coupon codes, no rewards — just wishes + confetti.
 *
 * Special variant: for user `ilarialacava105@gmail.com` (or when `?special=ilaria`
 * is passed for preview) the popup shows a personal message signed "Martino"
 * with additional animated pink hearts alongside the standard confetti.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import confetti from 'canvas-confetti';
import { X, PartyPopper, Cake, Heart } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const STORAGE_KEY = 'birthdayPopupSeenYear';
const ILARIA_EMAIL = 'ilarialacava105@gmail.com';

const BirthdayPopup = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [visible, setVisible] = useState(false);
  // Capture URL params at mount BEFORE we clean the URL, otherwise the
  // variant/force flags would be lost by history.replaceState.
  const [popupContext, setPopupContext] = useState({ isIlariaVariant: false });
  const confettiTimeoutsRef = useRef([]);

  useEffect(() => {
    if (!user) return;
    const params = new URLSearchParams(location.search);
    // Trigger paths:
    //   (A) explicit deep-link `?birthday=1` (from push notification tap or manual link)
    //   (B) IMPLICIT: user is Ilaria AND today (Europe/Rome) is her birthday
    //       23/08. This guarantees she sees the popup on first app open of
    //       the day even if she never taps the push notification.
    //   (C) preview override `?special=ilaria` (Martino testing)
    const explicit = params.get('birthday') === '1';
    const isIlariaEmail = (user.email || '').toLowerCase() === ILARIA_EMAIL;
    const previewOverride = params.get('special') === 'ilaria';

    // Compute "today in Rome" as MM-DD without needing tz libs on the client.
    const romeTodayMMDD = (() => {
      try {
        // `en-GB` gives DD/MM/YYYY layout; we split and rebuild MM-DD.
        const parts = new Intl.DateTimeFormat('en-GB', {
          timeZone: 'Europe/Rome', day: '2-digit', month: '2-digit',
        }).format(new Date()).split('/');
        return `${parts[1]}-${parts[0]}`;
      } catch { return null; }
    })();
    const ilariaAutoTrigger = isIlariaEmail && romeTodayMMDD === '08-23';

    if (!explicit && !previewOverride && !ilariaAutoTrigger) return;

    const isIlariaVariant = isIlariaEmail || previewOverride;

    // At-most-once per calendar year (skipped with ?force=1)
    const currentYear = new Date().getFullYear();
    try {
      if (localStorage.getItem(STORAGE_KEY) === String(currentYear) && !params.get('force')) {
        // Silently drop the query if present, and don't reopen.
        if (explicit || previewOverride) navigate('/', { replace: true });
        return;
      }
    } catch { /* localStorage disabled — proceed */ }

    setPopupContext({ isIlariaVariant });
    setVisible(true);
    try { localStorage.setItem(STORAGE_KEY, String(currentYear)); } catch { /* ignore */ }

    // Clean the URL so refresh doesn't retrigger the popup.
    try { window.history.replaceState({}, '', '/'); } catch { /* ignore */ }
  }, [user, location.search, navigate]);

  useEffect(() => {
    if (!visible) return;
    const { isIlariaVariant } = popupContext;
    const heartColors = ['#FF3B7F', '#FF6B9D', '#FF1F5F', '#FFB6C1', '#FF69B4', '#ffffff'];
    const cakeColors = ['#F5A623', '#FF6B6B', '#FFD700', '#FF9AE0', '#8AE1FF', '#ffffff'];
    const colors = isIlariaVariant ? heartColors : cakeColors;
    const heartShape = (() => {
      try { return [confetti.shapeFromPath({ path: 'M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z' })]; }
      catch { return undefined; }
    })();
    const fireBirthday = () => {
      confetti({ particleCount: 100, spread: 120, startVelocity: 55, origin: { x: 0.3, y: 0.25 }, colors, zIndex: 9999 });
      confetti({ particleCount: 100, spread: 120, startVelocity: 55, origin: { x: 0.7, y: 0.25 }, colors, zIndex: 9999 });
      if (isIlariaVariant && heartShape) {
        confetti({
          particleCount: 40, spread: 160, startVelocity: 30, gravity: 0.5,
          origin: { x: 0.5, y: 0 }, colors: heartColors,
          shapes: heartShape, scalar: 1.4, ticks: 300, zIndex: 9999,
        });
      }
    };
    fireBirthday();
    const t1 = setTimeout(fireBirthday, 500);
    const t2 = setTimeout(fireBirthday, 1100);
    const t3 = setTimeout(fireBirthday, 2000);
    const t4 = isIlariaVariant ? setTimeout(fireBirthday, 3200) : null;
    const t5 = isIlariaVariant ? setTimeout(fireBirthday, 4500) : null;
    confettiTimeoutsRef.current = [t1, t2, t3, t4, t5].filter(Boolean);
    return () => {
      confettiTimeoutsRef.current.forEach(clearTimeout);
      confetti.reset?.();
    };
  }, [visible, popupContext]);

  if (!visible) return null;

  const close = () => setVisible(false);
  const firstName = (user?.name || '').split(' ')[0];
  const { isIlariaVariant } = popupContext;

  return (
    <div
      className="fixed inset-0 z-[9000] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-[fadeIn_0.3s_ease-out]"
      data-testid="birthday-popup"
      onClick={close}
    >
      <style>{`
        @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
        @keyframes birthdayPopIn { 0% { opacity: 0; transform: scale(0.5) rotate(-8deg) } 60% { opacity: 1; transform: scale(1.08) rotate(3deg) } 100% { transform: scale(1) rotate(0deg) } }
        @keyframes cakeBounce { 0%, 100% { transform: translateY(0) rotate(-3deg) } 50% { transform: translateY(-8px) rotate(3deg) } }
        @keyframes shine { 0% { background-position: -200% center } 100% { background-position: 200% center } }
        @keyframes heartBeat { 0%, 100% { transform: scale(1) } 25% { transform: scale(1.18) } 50% { transform: scale(0.95) } 75% { transform: scale(1.15) } }
        @keyframes heartFloat0 { 0% { transform: translateY(0) rotate(-10deg); opacity: 0.85 } 100% { transform: translateY(-120px) rotate(20deg); opacity: 0 } }
        @keyframes heartFloat1 { 0% { transform: translateY(0) rotate(10deg); opacity: 0.85 } 100% { transform: translateY(-140px) rotate(-20deg); opacity: 0 } }
      `}</style>
      <div
        onClick={(e) => e.stopPropagation()}
        className={`relative w-full max-w-sm rounded-3xl border-2 shadow-2xl overflow-hidden ${
          isIlariaVariant
            ? 'bg-gradient-to-br from-[#3D0B25] via-[#1A0512] to-[#4D0A2E] border-[#FF3B7F]/60 shadow-[#FF3B7F]/40'
            : 'bg-gradient-to-br from-[#2A1B3D] via-[#1A1A1A] to-[#3D1B2A] border-[#F5A623]/50 shadow-[#F5A623]/40'
        }`}
        style={{ animation: 'birthdayPopIn 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) forwards' }}
      >
        {/* Close button */}
        <button
          onClick={close}
          className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-black/60 hover:bg-black/80 flex items-center justify-center text-white transition-colors"
          data-testid="birthday-popup-close-btn"
          aria-label="Chiudi"
        >
          <X className="w-5 h-5" />
        </button>

        {isIlariaVariant ? (
          <div
            className="relative min-h-[640px] bg-cover bg-center"
            style={{
              backgroundImage: 'url("/birthday/ilaria.jpg")',
            }}
          >
            {/* Bottom dark gradient overlay so text stays readable — keeps
                the photo clearly visible everywhere except at the bottom. */}
            <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/85 pointer-events-none" />

            {/* Floating hearts scattered over the photo */}
            <div className="pointer-events-none absolute inset-0 overflow-hidden">
              {[
                { l: '8%', b: '55%', d: 0, s: 22 },
                { l: '85%', b: '48%', d: 0.6, s: 20 },
                { l: '22%', b: '38%', d: 1.4, s: 18 },
                { l: '70%', b: '30%', d: 2.0, s: 24 },
                { l: '48%', b: '58%', d: 2.6, s: 18 },
                { l: '92%', b: '20%', d: 3.2, s: 22 },
                { l: '5%', b: '25%', d: 3.8, s: 20 },
              ].map((h, i) => (
                <Heart
                  key={i}
                  className="absolute text-[#FF3B7F] fill-[#FF3B7F]"
                  style={{
                    left: h.l, bottom: h.b, width: `${h.s}px`, height: `${h.s}px`,
                    animation: `heartFloat${i % 2} 3.5s ease-out ${h.d}s infinite`,
                    filter: 'drop-shadow(0 0 8px rgba(255,59,127,0.8))',
                  }}
                />
              ))}
            </div>

            {/* Textual content: title pinned to TOP (sky area of the photo),
                dedication + signature + button pinned to BOTTOM. Photo stays
                fully visible in the middle. */}
            <div className="relative min-h-[640px] flex flex-col p-5 text-center">
              {/* TOP block: title in white */}
              <div className="pt-2">
                <h1
                  className="font-black text-[2.1rem] leading-[0.95] text-white mb-1"
                  style={{
                    fontFamily: 'Barlow Condensed, Impact, sans-serif',
                    textShadow: '0 2px 8px rgba(0,0,0,0.95), 0 0 20px rgba(0,0,0,0.7), 2px 2px 0 rgba(0,0,0,0.9)',
                  }}
                >
                  BUON COMPLEANNO!
                </h1>
                <h2
                  className="font-black text-[2rem] leading-none text-white uppercase tracking-tight inline-flex items-center justify-center gap-2"
                  style={{
                    fontFamily: 'Barlow Condensed, Impact, sans-serif',
                    textShadow: '0 2px 8px rgba(0,0,0,0.95), 0 0 20px rgba(0,0,0,0.7), 2px 2px 0 rgba(0,0,0,0.9)',
                  }}
                >
                  TI AMO
                  <Heart
                    className="w-6 h-6 text-[#FF3B7F] fill-[#FF3B7F]"
                    style={{ animation: 'heartBeat 1.4s ease-in-out infinite', filter: 'drop-shadow(0 0 6px rgba(0,0,0,0.9))' }}
                  />
                </h2>
              </div>

              {/* Flexible spacer to push the bottom block to the bottom */}
              <div className="flex-1" />

              {/* BOTTOM block: dedication + signature + button */}
              <div className="pb-1">
                <p
                  className="text-white text-[12px] leading-snug mb-2 px-1 italic font-medium"
                  style={{ textShadow: '0 1px 3px #000, 0 0 8px rgba(0,0,0,0.9), -1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000' }}
                >
                  Auguri a te, che se non ci fossi non so che farei... Che ogni giorno con il tuo amore, la tua pazienza, la tua cura, riesci a darmi il coraggio di sognare.
                </p>
                <p
                  className="text-white text-[12px] leading-snug mb-2 px-1 italic font-medium"
                  style={{ textShadow: '0 1px 3px #000, 0 0 8px rgba(0,0,0,0.9), -1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000' }}
                >
                  Nel giorno del tuo compleanno, un anno fa, senza saperlo, sei tu che hai fatto un regalo a me.
                </p>
                <p
                  className="text-white text-[12px] font-extrabold leading-snug mb-3 px-1 uppercase tracking-wider"
                  style={{ textShadow: '0 1px 3px #000, 0 0 8px rgba(0,0,0,0.95), -1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000' }}
                >
                  Il regalo più bello di tutta la mia vita sei tu
                </p>

                {/* Signature */}
                <p
                  className="text-white text-2xl mb-4"
                  style={{
                    fontFamily: '"Dancing Script", "Great Vibes", "Brush Script MT", cursive',
                    fontStyle: 'italic',
                    textShadow: '0 2px 4px #000, 0 0 12px rgba(0,0,0,0.9), -1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000',
                  }}
                >
                  Martino
                </p>

                {/* Thanks button */}
                <button
                  onClick={close}
                  className="w-full py-3 rounded-xl bg-gradient-to-r from-[#FF3B7F] via-[#FF6B9D] to-[#FF3B7F] text-white font-black uppercase tracking-[0.2em] text-sm shadow-lg shadow-[#FF3B7F]/50 hover:brightness-110 active:scale-[0.98] transition-all"
                  data-testid="birthday-popup-thanks-btn"
                >
                  Grazie ❤️
                </button>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Cake illustration */}
            <div className="pt-8 pb-2 flex items-center justify-center">
              <div
                className="w-24 h-24 rounded-full bg-gradient-to-br from-[#F5A623] to-[#FF6B6B] flex items-center justify-center shadow-lg shadow-[#F5A623]/50"
                style={{ animation: 'cakeBounce 2s ease-in-out infinite' }}
              >
                <Cake className="w-12 h-12 text-white" strokeWidth={2.5} />
              </div>
            </div>

            {/* Wishes */}
            <div className="px-6 pt-3 pb-6 text-center">
              <h1
                className="font-black text-[2.3rem] leading-[0.95] text-transparent bg-clip-text bg-gradient-to-b from-[#FFD86B] via-[#F5A623] to-[#FF6B6B] mb-2"
                style={{ fontFamily: 'Barlow Condensed, Impact, sans-serif' }}
              >
                BUON COMPLEANNO{firstName ? ',' : '!'}
              </h1>
              {firstName && (
                <h2
                  className="font-black text-[1.8rem] leading-none text-white uppercase tracking-tight mb-4"
                  style={{ fontFamily: 'Barlow Condensed, Impact, sans-serif' }}
                >
                  {firstName}!
                </h2>
              )}

              <div className="flex items-center justify-center gap-2 mb-4">
                <PartyPopper className="w-5 h-5 text-[#F5A623]" />
                <p
                  className="text-[#F5A623] text-xs font-extrabold uppercase tracking-[0.3em]"
                  style={{
                    background: 'linear-gradient(90deg, #F5A623, #FFD700, #FF6B6B, #FFD700, #F5A623)',
                    backgroundSize: '200% 100%',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    animation: 'shine 3s linear infinite',
                  }}
                >
                  Da RVincente
                </p>
                <PartyPopper className="w-5 h-5 text-[#F5A623]" />
              </div>

              <p className="text-gray-300 text-sm leading-relaxed mb-6 px-2">
                Un in bocca al lupo speciale per i tuoi pronostici! 🍀
              </p>

              {/* Thanks button */}
              <button
                onClick={close}
                className="w-full py-3.5 rounded-xl bg-gradient-to-r from-[#F5A623] via-[#FF6B6B] to-[#F5A623] text-[#1A1A1A] font-black uppercase tracking-[0.2em] text-sm shadow-lg shadow-[#F5A623]/40 hover:brightness-110 active:scale-[0.98] transition-all"
                data-testid="birthday-popup-thanks-btn"
              >
                Grazie! 🎉
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default BirthdayPopup;
